import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import { FaUserGraduate, FaLock, FaSignInAlt } from "react-icons/fa";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import { firestore } from "../Firebase";
import Bg from "../Images/about.jpg";

const MemberLogin = () => {
  const [studentNumber, setStudentNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": { borderColor: "rgba(0, 217, 255, 0.3)" },
      "&:hover fieldset": { borderColor: "rgba(0, 217, 255, 0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#00d9ff" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00d9ff" },
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Query Firestore for member with matching student number
      const snapshot = await firestore
        .collection("registrations")
        .where("studentNumber", "==", studentNumber)
        .get();

      if (snapshot.empty) {
        setError("Student number not found. Please register first.");
        setLoading(false);
        return;
      }

      const memberDoc = snapshot.docs[0];
      const memberData = memberDoc.data();

      // Check password
      if (memberData.password !== password) {
        setError("Incorrect password. Please try again.");
        setLoading(false);
        return;
      }

      // Store member data in localStorage
      const memberSession = {
        id: memberDoc.id,
        fullName: memberData.fullName,
        studentNumber: memberData.studentNumber,
        program: memberData.program,
        yearOfStudy: memberData.yearOfStudy,
        status: memberData.status || "pending",
        phoneNumber: memberData.phoneNumber,
      };

      localStorage.setItem("member", JSON.stringify(memberSession));
      navigate("/Client/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card sx={{ borderRadius: "25px", ...darkGlassStyle, overflow: "hidden" }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              {/* Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "20px",
                      background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                      boxShadow: "0 10px 30px rgba(0, 217, 255, 0.4)",
                    }}
                  >
                    <FaUserGraduate size={40} color="white" />
                  </Box>
                </motion.div>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    background: "linear-gradient(90deg, #ffffff, #00d9ff)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Member Login
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)", mt: 1 }}>
                  Access your ICTAZ NIPA member portal
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: "12px",
                    background: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    color: "#ef4444",
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Login Form */}
              <form onSubmit={handleLogin}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <TextField
                    label="Student Number"
                    value={studentNumber}
                    onChange={(e) => setStudentNumber(e.target.value)}
                    required
                    fullWidth
                    sx={inputStyle}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaUserGraduate color="#00d9ff" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    sx={inputStyle}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaLock color="#00d9ff" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: "rgba(255,255,255,0.7)" }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "0 4px 20px rgba(0, 217, 255, 0.4)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #00b4d8, #0096c7)",
                        },
                        "&:disabled": {
                          background: "rgba(0, 217, 255, 0.3)",
                        },
                      }}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FaSignInAlt />}
                    >
                      {loading ? "Signing In..." : "Sign In"}
                    </Button>
                  </motion.div>
                </Box>
              </form>

              {/* Register Link */}
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  Don't have an account?{" "}
                  <Link
                    to="/Client/registration"
                    style={{
                      color: "#00d9ff",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    Register Now
                  </Link>
                </Typography>
              </Box>

              {/* Info Box */}
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  borderRadius: "12px",
                  background: "rgba(0, 217, 255, 0.1)",
                  border: "1px solid rgba(0, 217, 255, 0.2)",
                }}
              >
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center" }}>
                  After registration, pay <strong style={{ color: "#00d9ff" }}>K30</strong> to the Treasurer to activate your account and access member resources.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default MemberLogin;
